import { useState } from "react";
import { useAppSelector } from "app/hooks";
import { Box, ClickAwayListener, Collapse, Link, Theme, Typography } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { CompanySummaryBody, CompanySummaryHeader } from "components/CompanySummary";
import CustomIcon from "components/CustomIcon";
import HeaderNavigation from "components/HeaderNavigation";
import { NavButtonProps } from "components/HeaderNavigation/NavButton";
import icons from "enums/icons";

import { getProjectCompanyData } from "store/slice/Project/projectData/ProjectSlice";

const customHeaderStyles = makeStyles((theme: Theme) =>
    createStyles({
        companySummaryContainer: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            gap: 2,
            padding: theme.spacing(2, 4),
            backgroundColor: theme.palette.background.blue,
        },
        summaryHeaderCompanyLink: {
            display: "flex",
            alignItems: "center",
            textDecoration: "underline",
            textUnderlineOffset: "4px",
            color: theme.palette.primary.main,
            fontWeight: 400,
            fontSize: 12,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            "& .MuiTooltip-popper": {
                backgroundColor: theme.palette.others.darkerColor,
            },
        },
        projectionHeaderHiddenPanel: {
            backgroundColor: theme.palette.background.blue,
            padding: theme.spacing(0, 2.2),
            position: "absolute",
            zIndex: -1,
            top: "100%",
            left: 0,
            width: "100%",
            boxShadow: "0 2px 10px #646464",
        },
        linkIcon: {
            width: 14,
            height: 14,
            marginLeft: `${theme.spacing(1)} !important`,
        },
    })
);

interface ICustomHeaderProps {
    title: string;
    backButton: NavButtonProps;
    nextButton: NavButtonProps;
    middlePlaceholder?: JSX.Element | boolean;
    isTeamHeader?: boolean;
}
const CustomHeader = ({ title, backButton, nextButton, middlePlaceholder, isTeamHeader }: ICustomHeaderProps) => {
    const classes = customHeaderStyles();

    const { data: companyData } = useAppSelector(getProjectCompanyData);

    const [isCompanyInfoVisible, setIsCompanyInfoVisible] = useState<boolean>(false);

    const toggleCompanyInfo = () => {
        setIsCompanyInfoVisible(!isCompanyInfoVisible);
    };

    const handleClickAway = () => {
        setIsCompanyInfoVisible(false);
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ position: "relative", zIndex: 10 }}>
                <Box className={classes.companySummaryContainer}>
                    <Box sx={{ justifySelf: "start", minWidth: 0 }}>
                        <Typography variant="h4" sx={{ marginBottom: 2 }}>
                            {title}
                        </Typography>
                        <CompanySummaryHeader
                            showAvatar
                            title={companyData?.name ?? ""}
                            link={
                                isTeamHeader ? (
                                    <Link onClick={toggleCompanyInfo} className={classes.summaryHeaderCompanyLink}>
                                        {companyData?.name}
                                        <CustomIcon
                                            className={classes.linkIcon}
                                            icon={
                                                isCompanyInfoVisible ? icons.circledChevronUp : icons.circledChevronDown
                                            }
                                        />
                                    </Link>
                                ) : undefined
                            }
                        />
                    </Box>

                    {middlePlaceholder && middlePlaceholder}

                    <Box sx={{ justifySelf: "end" }}>
                        <HeaderNavigation backButton={backButton} nextButton={nextButton} />
                    </Box>
                </Box>
                <Collapse in={isCompanyInfoVisible} className={classes.projectionHeaderHiddenPanel}>
                    <CompanySummaryBody companyData={companyData} />
                </Collapse>
            </Box>
        </ClickAwayListener>
    );
};

export default CustomHeader;
